/* eslint-disable class-methods-use-this */
import type { Runtime } from './enum-type';
import type { KNB as KNBInerface } from '../types/KNB';
import { webLogin } from './utils';

export class Bridge {
  KNBP: KNBInerface<'promise'> & typeof KNBInerface;

  isTitans: boolean = false;

  constructor() {
    this.KNBP = KNB.getPromiseInstance();
    this.isTitans = (KNB && KNB.env && KNB.env.isTitans) || false;
  }

  static setTitle(title: string) {
    document.title = title;
  }

  openPage(url: string) {
    if (!url) {
      return;
    }
    if (this.isTitans) {
      KNB.ready(() => {
        KNB.openPage({
          url, // 需要打开的完整http链接
          // 用于配置容器行为参数，附加在跳转协议上
          query: {
            _knbopeninapp: 1,
            noquery: 1,
          },
        });
      });
    } else {
      window.location.href = url;
    }
  }

  jumpWebview(url: string) {
    KNB.ready(() => {
      KNB.jumpWebview({ url });
    });
  }

  setStorage(
    {
      key,
      value,
      success,
      fail,
    }: {
      key: string;
      value: string;
      success?: () => void;
      fail?: () => void;
    } = { key: '', value: '' }
  ) {
    KNB.ready(() => {
      if (KNB.setStorage) {
        KNB.setStorage({
          key, // 数据键，String类型
          value, // 数据值，String类型
          success,
          fail,
        });
      }
    });
  }

  // 通知信息到native
  notifyNative(knbFunc: string, msg: object) {
    KNB.ready(() => {
      KNB.use(knbFunc, msg);
    });
  }

  // 读取互通存储
  getStorage(
    key: string,
    success: (result: any) => void,
    fail: (result: any) => void
  ): Promise<any> {
    if (!this.isTitans) {
      return Promise.resolve();
    }
    return this.KNBP.getStorage({ key }).then(success, fail);
  }

  clearStorage(key: string) {
    KNB.ready(() => {
      if (KNB.clearStorage) {
        KNB.clearStorage({
          key, // 数据键，String类型
          success: () => {
            // empty
          },
        });
      }
    });
  }

  closeWebview() {
    if (this.isTitans) {
      KNB.closeWebview({});
    } else {
      window.history.back();
    }
  }

  getLocation() {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.getLocation({
          timeout: 8000,
          sceneToken: 'jf-4b58aa4469ef6adb',
          success: (result: any) => {
            resolve(result);
          },
        });
      });
    });
  }

  getFingerprint() {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.getFingerprint({
          success: (result: any) => {
            resolve(result.fingerprint);
          },
        });
      });
    });
  }

  /**
   * 获取支付 SDK 版本号
   */
  getPaymentSDKVersion() {
    return new Promise((resolve, reject) => {
      KNB.ready(() => {
        KNB.use('getServiceInfo', {
          name: 'payment',
          success: (result: any) => {
            // payment 版本
            if (result && result.version) {
              resolve(result.version);
            } else {
              resolve('0.0.0');
            }
          },
          fail: (result: any) => {
            // 如果当前 name 不支持，则会返回 -403
            reject(result);
          },
        });
      });
    });
  }

  /**
   * appear 订阅消息
   */
  subscribeAction(handle: () => void, action: string = 'appear') {
    return new Promise((resolve, reject) => {
      if (!this.isTitans) {
        resolve(null);
      }
      KNB.unsubscribe({
        action, // 取消该所有事件的订阅
        success: resolve,
        fail: reject,
      });
      KNB.subscribe({
        action,
        handle,
        success: resolve,
        fail: reject,
      });
    });
  }

  /**
   * 取消 appear 订阅消息
   */
  unsubscribeAction(action: string = 'appear') {
    return new Promise((resolve, reject) => {
      if (!this.isTitans) {
        resolve(null);
      }
      KNB.unsubscribe({
        action, // 取消该所有当前事件的订阅
        success: resolve,
        fail: reject,
      });
    });
  }

  setLLButton(icon: string, handle: () => void) {
    if (this.isTitans) {
      this.KNBP.setLLButton({ icon, handle })
        .then(() => {
          console.log('注册成功');
        })
        .catch((error) => {
          return error;
        });
    }
  }

  setBouncesEnabled(enabled: 0 | 1 | undefined = 0) {
    if (this.isTitans) {
      this.KNBP.setBouncesEnabled({ enabled }).catch((error) => {
        console.error(error);
      });
    }
  }

  /**
   * 点评下需要通过 setLLButton， setTitle 做兼容
   */
  setNavigationBarHidden(flag: 0 | 1 = 1) {
    if (this.isTitans) {
      this.KNBP.setNavigationBarHidden({ flag }).catch((error) => {
        console.error(error);
      });
    }
  }

  /**
   * style = 1:状态栏字体为白色, 0:状态栏字体为黑色,官网表示只有 iOS支持，但是实际测试发现 Android 也是支持的
   * backgroundColor 状态栏背景色，rgba 色值，仅 Android 在 titans >= 11.18.7 支持
   */
  setStatusBarStyle() {
    if (this.isTitans) {
      this.KNBP.setStatusBarStyle({ style: 0 }).catch((error) => {
        console.error(error);
      });
    }
  }

  closePage() {
    KNB.closePage({});
  }

  resetLLButton() {
    if (this.isTitans) {
      this.KNBP.setNavButtons([
        {
          position: 'LL',
          disable: 1,
        },
      ]);
    }
  }

  setLLButtonText(text: string, handle: () => void) {
    KNB.ready(() => {
      KNB.setLLButton({
        type: 'text',
        text,
        handle,
        success: () => {
          // empty
        },
      });
    });
  }

  setLRButtonText(text: string, handle: () => void) {
    KNB.ready(() => {
      KNB.setLRButton({
        type: 'text',
        text,
        handle,
        success: () => {
          // empty
        },
      });
    });
  }

  setRRButtonText(text: string, handle: () => void) {
    KNB.ready(() => {
      KNB.setRRButton({
        type: 'text',
        text,
        handle,
        success: () => {
          // empty
        },
      });
    });
  }

  login(runtime: Runtime) {
    if (this.isTitans) {
      KNB.login();
    } else {
      webLogin(runtime);
    }
  }
}
export const bridge = new Bridge();
